import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, endOfMonth } from "date-fns";
import { COUNTING_PICKUP_STATUSES } from "@/lib/pickupStatus";

interface PickupLimits {
  maxPickups: number;
  usedPickups: number;
  remainingPickups: number;
  canSchedule: boolean;
  subscriptionType: string | null;
  loading: boolean;
}

export const usePickupLimits = (userId: string | undefined): PickupLimits & { refetch: () => Promise<void> } => {
  const [limits, setLimits] = useState<PickupLimits>({
    maxPickups: 1,
    usedPickups: 0,
    remainingPickups: 1,
    canSchedule: true,
    subscriptionType: null,
    loading: true,
  });

  const fetchLimits = useCallback(async () => {
    if (!userId) {
      setLimits(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      // Fetch user profile for subscription type
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("subscription_type")
        .eq("id", userId)
        .maybeSingle();

      if (profileError) throw profileError;

      const subscriptionType = profile?.subscription_type || null;
      
      // Determine max pickups based on subscription
      const maxPickups = getMaxPickups(subscriptionType);

      // Count pickups this billing period that count toward limit
      // Uses consolidated status constants from pickupStatus.ts
      const now = new Date();
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);

      const { data: pickups, error: pickupsError } = await supabase
        .from("user_pickups")
        .select("id, status")
        .eq("user_id", userId)
        .gte("proposed_date", monthStart.toISOString().split("T")[0])
        .lte("proposed_date", monthEnd.toISOString().split("T")[0]);

      if (pickupsError) throw pickupsError;

      // Count pickups that count toward limit using consolidated constants
      const usedPickups = (pickups || []).filter(
        (p) => COUNTING_PICKUP_STATUSES.includes(p.status as any)
      ).length;

      const remainingPickups = Math.max(0, maxPickups - usedPickups);
      const canSchedule = remainingPickups > 0;

      setLimits({
        maxPickups,
        usedPickups,
        remainingPickups,
        canSchedule,
        subscriptionType,
        loading: false,
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching pickup limits:", error);
      }
      setLimits(prev => ({ ...prev, loading: false }));
    }
  }, [userId]);

  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  return { ...limits, refetch: fetchLimits };
};

// Helper to determine max pickups based on subscription type
function getMaxPickups(subscriptionType: string | null): number {
  if (!subscriptionType) return 1;
  
  const type = subscriptionType.toLowerCase();
  
  // Bi-weekly plan gets 2 pickups per month
  if (type.includes("bi-weekly") || type.includes("biweekly") || type.includes("2x")) {
    return 2;
  }
  
  // Monthly plan gets 1 pickup per month
  if (type.includes("monthly") || type.includes("1x")) {
    return 1;
  }
  
  // One-time plan gets 1 pickup
  if (type.includes("one-time") || type.includes("one time") || type.includes("single")) {
    return 1;
  }
  
  // Default to 1
  return 1;
}
